import { useMemo } from "react";
import { useBodyPalette } from "../../scene/BodyFinish";
import { Chassis } from "../../scene/Chassis";
import { GLASS, HEADLIGHT, TAIL_LIGHT } from "../../scene/materials";
import { panelBetween } from "../geometry/panels";
import { buildShell } from "../geometry/shell";
import {
  lastAxle,
  axleHalfTrack,
  TRUCK_10,
  TRUCK_12,
  TRUCK_16,
  TRUCK_4,
  TRUCK_6,
  TRUCK_6_THREE_SINGLE
} from "./dimensions";
import { cabProfile } from "./profiles";
const CAB_BEVEL = 0.08;
const CAB_SURFACE = CAB_BEVEL + 0.02;
const cabExtrude = (d) => ({
  depth: d.cabWidth - CAB_BEVEL * 2,
  bevelEnabled: true,
  bevelThickness: CAB_BEVEL,
  bevelSize: CAB_BEVEL,
  bevelSegments: 4,
  curveSegments: 24,
  steps: 1
});
const CHASSIS_RAIL_Z = 0.44;
const RAIL_DEPTH = 0.26;
function TruckBody({ dimensions: d }) {
  const palette = useBodyPalette("truck");
  const settings = useMemo(() => cabExtrude(d), [d]);
  const cab = useMemo(
    () => buildShell(cabProfile(d), settings, { depth: settings.depth }),
    [d, settings]
  );
  const nose = d.length / 2;
  const tail = -nose;
  const cargoLength = d.cargo.frontX - d.cargo.rearX;
  const cargoCentre = (d.cargo.frontX + d.cargo.rearX) / 2;
  const wallCentreY = d.cargo.floorY + 0.1 + d.cargo.wallHeight / 2;
  const railCentreY = d.chassisY - RAIL_DEPTH / 2;
  const railFront = nose - 0.5;
  const railRear = tail + 0.1;
  const windscreen = useMemo(
    () => panelBetween(d.windscreenLower, d.windscreenUpper, d.cabWidth - 0.22, 0.03, CAB_SURFACE),
    [d]
  );
  const rearmost = lastAxle(d);
  const rearmostDriveAxle = rearmost.x;
  const mudflapTrack = axleHalfTrack(d, rearmost);
  return <group>
      <Chassis>
        {
    /* Chassis rails, visible below the cargo body between the wheels. */
  }
        {[-CHASSIS_RAIL_Z, CHASSIS_RAIL_Z].map((z) => <mesh
    key={`rail-${z}`}
    position={[(railFront + railRear) / 2, railCentreY, z]}
  >
            <boxGeometry args={[railFront - railRear, RAIL_DEPTH, 0.1]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
        {d.crossmemberX.map((x) => <mesh key={`crossmember-${x}`} position={[x, railCentreY, 0]}>
            <boxGeometry args={[0.08, 0.16, CHASSIS_RAIL_Z * 2]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
      </Chassis>

      <mesh geometry={cab}>
        <meshPhysicalMaterial {...palette.body} />
      </mesh>

      <mesh position={windscreen.position} rotation={windscreen.rotation}>
        <boxGeometry args={windscreen.args} />
        <meshPhysicalMaterial {...GLASS} />
      </mesh>

      {
    /*
      The cab flanks are flat planes at exactly ±cabWidth/2 because the cab is not
      tapered, so a thin box lies perfectly flush against them.
    */
  }
      {[-1, 1].map((side) => <mesh
    key={`side-window-${side}`}
    position={[
      (d.sideWindow.frontX + d.sideWindow.rearX) / 2,
      (d.sideWindow.lowY + d.sideWindow.highY) / 2,
      side * (d.cabWidth / 2 + 4e-3)
    ]}
  >
          <boxGeometry
    args={[
      d.sideWindow.frontX - d.sideWindow.rearX,
      d.sideWindow.highY - d.sideWindow.lowY,
      0.02
    ]}
  />
          <meshPhysicalMaterial {...GLASS} />
        </mesh>)}

      {
    /* sun visor above the windscreen */
  }
      <mesh position={[d.windscreenUpper[0] - 0.16, d.cabRoofY + CAB_SURFACE, 0]}>
        <boxGeometry args={[0.3, 0.07, d.cabWidth - 0.05]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>

      {
    /* grille and headlamps on the front panel */
  }
      <mesh position={[nose + CAB_SURFACE, d.cabFloorY + 0.86, 0]}>
        <boxGeometry args={[0.1, 0.6, d.cabWidth - 0.5]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {[-1, 1].map((side) => <mesh
    key={`headlight-${side}`}
    position={[nose + CAB_SURFACE, d.cabFloorY + 0.42, side * (d.cabWidth / 2 - 0.34)]}
  >
          <boxGeometry args={[0.1, 0.24, 0.44]} />
          <meshPhysicalMaterial {...HEADLIGHT} />
        </mesh>)}

      {
    /* low front bumper, hanging below the cab as it does on a real truck */
  }
      <mesh position={[nose + 0.06, 0.7, 0]}>
        <boxGeometry args={[0.18, 0.28, d.cabWidth + 0.02]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>

      {
    /* tall door mirrors */
  }
      {[-1, 1].map((side) => <group key={`mirror-${side}`}>
          <mesh position={[nose - 0.2, d.cabRoofY - 0.6, side * (d.cabWidth / 2 + 0.09)]}>
            <boxGeometry args={[0.05, 0.05, 0.2]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
          <mesh position={[nose - 0.24, d.cabRoofY - 0.78, side * (d.cabWidth / 2 + 0.21)]}>
            <boxGeometry args={[0.09, 0.44, 0.15]} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
        </group>)}

      {
    /*
      Cargo floor slab is SHELL despite a chassis-coloured material: it is the mesh a ray hits
      on its way to an inner dual. Material identity is the wrong classifier.
    */
  }
      <mesh position={[cargoCentre, d.cargo.floorY + 0.05, 0]}>
        <boxGeometry args={[cargoLength, 0.1, d.bodyWidth]} />
        <meshPhysicalMaterial {...palette.chassis} />
      </mesh>
      {[-1, 1].map((side) => <mesh
    key={`cargo-side-${side}`}
    position={[cargoCentre, wallCentreY, side * (d.bodyWidth / 2 - 0.035)]}
  >
          <boxGeometry args={[cargoLength, d.cargo.wallHeight, 0.07]} />
          <meshPhysicalMaterial {...palette.secondary} />
        </mesh>)}
      <mesh
    position={[d.cargo.frontX - 0.04, d.cargo.floorY + 0.1 + (d.cargo.wallHeight + 0.12) / 2, 0]}
  >
        <boxGeometry args={[0.08, d.cargo.wallHeight + 0.12, d.bodyWidth]} />
        <meshPhysicalMaterial {...palette.secondary} />
      </mesh>
      <mesh position={[d.cargo.rearX + 0.04, wallCentreY, 0]}>
        <boxGeometry args={[0.08, d.cargo.wallHeight, d.bodyWidth]} />
        <meshPhysicalMaterial {...palette.secondary} />
      </mesh>

      <Chassis>
        {
    /* fuel tank one side, air reservoir the other */
  }
        <mesh position={[0.45, railCentreY - 0.06, -0.8]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.24, 0.24, 0.95, 24]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>
        <mesh position={[0.3, railCentreY - 0.04, 0.8]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.14, 0.14, 0.7, 20]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>
      </Chassis>

      <Chassis>
        {
    /* mud flaps behind the rearmost drive axle, in place of full mudguards */
  }
        {[-1, 1].map((side) => <mesh
    key={`mudflap-${side}`}
    position={[rearmostDriveAxle - d.wheelRadius - 0.16, 0.5, side * mudflapTrack]}
  >
            <boxGeometry args={[0.03, 0.36, 0.66]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
      </Chassis>

      {
    /* tail lamps and rear underrun bar */
  }
      {[-1, 1].map((side) => <mesh
    key={`tail-light-${side}`}
    position={[d.cargo.rearX - 0.03, d.cargo.floorY + 0.3, side * (d.bodyWidth / 2 - 0.3)]}
  >
          <boxGeometry args={[0.09, 0.24, 0.34]} />
          <meshPhysicalMaterial {...TAIL_LIGHT} />
        </mesh>)}
      <Chassis>
        <mesh position={[tail + 0.12, 0.6, 0]}>
          <boxGeometry args={[0.1, 0.14, d.bodyWidth - 0.3]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
        {[-1, 1].map((side) => <mesh
    key={`underrun-hanger-${side}`}
    position={[tail + 0.14, 0.9, side * (d.bodyWidth / 2 - 0.5)]}
  >
            <boxGeometry args={[0.07, 0.5, 0.09]} />
            <meshPhysicalMaterial {...palette.chassis} />
          </mesh>)}
      </Chassis>

      {[-0.55, 0, 0.55].map((z) => <mesh
    key={`roof-marker-${z}`}
    position={[d.windscreenUpper[0] - 0.45, d.cabRoofY + 0.06, z]}
  >
          <boxGeometry args={[0.08, 0.06, 0.14]} />
          <meshPhysicalMaterial {...palette.accent} />
        </mesh>)}
      {[-1, 1].map((side) => <mesh
    key={`side-underrun-${side}`}
    position={[cargoCentre, 0.52, side * (d.bodyWidth / 2 - 0.02)]}
  >
          <boxGeometry args={[cargoLength * 0.88, 0.07, 0.05]} />
          <meshPhysicalMaterial {...palette.edge} />
        </mesh>)}
    </group>;
}
const Truck6Body = () => <TruckBody dimensions={TRUCK_6} />;
const Truck10Body = () => <TruckBody dimensions={TRUCK_10} />;
const Truck12Body = () => <TruckBody dimensions={TRUCK_12} />;
const Truck16Body = () => <TruckBody dimensions={TRUCK_16} />;
const Truck6ThreeSingleBody = () => <TruckBody dimensions={TRUCK_6_THREE_SINGLE} />;
const Truck4Body = () => <TruckBody dimensions={TRUCK_4} />;
export {
  Truck10Body,
  Truck12Body,
  Truck16Body,
  Truck4Body,
  Truck6Body,
  Truck6ThreeSingleBody
};
